import React, { useCallback, useEffect, useRef, useState } from "react";
import { Toaster } from "../../../common/Toaster/Toaster";

/**
 * Per-action copy and which inputs to show. Keyed by the `action` prop.
 * @type {Object<string, { title: string, submit: string, blurb?: string }>}
 */
const ACTIONS = {
  escalate: {
    title: "Escalate ticket",
    submit: "Escalate",
    blurb: "Bring a second agent onto this ticket.",
  },
  snooze: {
    title: "Snooze ticket",
    submit: "Snooze",
    blurb:
      "The ticket leaves the open queue and the SLA sweep skips it until it wakes. The room stays writable, so a customer reply is never dropped.",
  },
  unassign: {
    title: "Return to the pool",
    submit: "Return to pool",
    blurb: "Any agent will be able to pick this ticket up.",
  },
};

/** `datetime-local` needs a local-time string, not an ISO/UTC one. */
const toLocalInputValue = (date) => {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

/** Default snooze target: tomorrow morning, in the agent's own timezone. */
const defaultSnoozeUntil = () => {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  tomorrow.setHours(9, 0, 0, 0);
  return toLocalInputValue(tomorrow);
};

/**
 * One small modal covering escalate / snooze / unassign.
 *
 * Merge is deliberately not here: picking a ticket by typing its room id is
 * not a usable way to choose a duplicate. It has its own picker in
 * `MergeTicketDialog`.
 *
 * This exists because the actions used to run off `window.prompt`, which blocks
 * the event loop — and with it every Centrifugo publication handler — until the
 * agent dismisses it. Follows the outside-click + body-lock pattern of
 * `CreateTicketModal`.
 *
 * @param {Object} props
 * @param {'escalate'|'snooze'|'unassign'|null} props.action - Null closes the modal.
 * @param {() => void} props.onClose
 * @param {(payload: Object) => Promise<void>|void} props.onSubmit - Receives the
 *   action's payload: `{ reason }` or `{ until, reason }`.
 * @param {Array<{ userId: string, name: string }>} [props.agents] - Escalation targets.
 */
export default function TicketActionModal({ action, onClose, onSubmit, agents = [] }) {
  const [reason, setReason] = useState("");
  const [agentUserId, setAgentUserId] = useState("");
  const [until, setUntil] = useState(defaultSnoozeUntil);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const modalRef = useRef(null);

  const isOpen = Boolean(action);
  const meta = ACTIONS[action] || null;

  const resetAndClose = useCallback(() => {
    setReason("");
    setAgentUserId("");
    setUntil(defaultSnoozeUntil());
    setSubmitting(false);
    setErrorMessage("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "auto";
      return undefined;
    }
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        resetAndClose();
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") resetAndClose();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, resetAndClose]);

  const flashError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 3000);
  };

  const buildPayload = () => {
    if (action === "snooze") {
      if (!until) {
        flashError("Pick when this should come back.");
        return null;
      }
      // `datetime-local` yields local wall time; the API wants an instant.
      return { until: new Date(until).toISOString(), reason: reason.trim() || undefined };
    }
    if (action === "escalate") {
      return {
        agent_user_id: agentUserId || undefined,
        reason: reason.trim() || undefined,
      };
    }
    return { reason: reason.trim() || undefined };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      await onSubmit(payload);
      resetAndClose();
    } catch (err) {
      setSubmitting(false);
      flashError(err?.message || "That didn't work. Try again.");
    }
  };

  if (!isOpen || !meta) return null;

  const fieldClass =
    "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-primary/40";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {errorMessage && <Toaster message={errorMessage} type="error" />}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={meta.title}
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
      >
        <h2 className="text-base font-bold text-gray-800">{meta.title}</h2>
        {meta.blurb && <p className="mt-1 text-xs text-gray-500">{meta.blurb}</p>}

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {action === "escalate" && agents.length > 0 && (
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">
                Escalate to
              </span>
              <select
                value={agentUserId}
                onChange={(e) => setAgentUserId(e.target.value)}
                className={fieldClass}
              >
                <option value="">Anyone available</option>
                {agents.map((agent) => (
                  <option key={agent.userId} value={agent.userId}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {action === "snooze" && (
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">
                Wake it up at
              </span>
              <input
                type="datetime-local"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className={fieldClass}
              />
            </label>
          )}


          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">
              Reason <span className="font-normal text-gray-400">(optional)</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className={fieldClass}
            />
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={resetAndClose}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-orange-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Working…" : meta.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
